import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { useAudio } from '../../context/AudioContext';

interface Props {
  onClick: () => void;
  hasUnreadMessages?: boolean;
}

const pulse = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(255, 105, 180, 0.7);
  }
  70% {
    transform: scale(1.05);
    box-shadow: 0 0 0 10px rgba(255, 105, 180, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(255, 105, 180, 0);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-3px);
  }
`;

const ChatButton = styled(motion.button)<{ $hasUnread?: boolean; $hasAudio?: boolean }>`
  position: fixed;
  bottom: ${props => props.$hasAudio ? '116px' : '24px'}; /* Move above player when audio is playing */
  right: 24px;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ff69b4, #ffb6c1);
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  z-index: 10000; /* Ensure above footer (z-index: 5) and all other elements */
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  animation: ${float} 3s ease-in-out infinite;

  &:hover {
    transform: scale(1.1) translateY(-2px);
    box-shadow: 0 8px 25px rgba(255, 105, 180, 0.4);
  }

  &:active {
    transform: scale(0.95);
  }

  ${props => props.$hasUnread && `
    animation: ${pulse} 2s infinite, ${float} 3s ease-in-out infinite;
  `}

  @media (max-width: 768px) {
    bottom: ${props => props.$hasAudio ? '160px' : '20px'}; /* Increased clearance for mobile player */
    right: 20px;
    width: 56px;
    height: 56px;
    font-size: 1.3rem;
    z-index: 10001; /* Extra high z-index on mobile to ensure visibility */
  }
`;

const NotificationBadge = styled.div<{ $visible: boolean }>`
  position: absolute;
  top: -2px;
  right: -2px;
  width: 18px;
  height: 18px;
  background: #ff4444;
  border-radius: 50%;
  border: 2px solid #1a0f08;
  opacity: ${props => props.$visible ? 1 : 0};
  transform: scale(${props => props.$visible ? 1 : 0});
  transition: all 0.3s ease;
`;

const Tooltip = styled.div<{ $visible: boolean }>`
  position: absolute;
  right: 70px;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.9);
  color: #f4e4c1;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 0.9rem;
  white-space: nowrap;
  opacity: ${props => props.$visible ? 1 : 0};
  visibility: ${props => props.$visible ? 'visible' : 'hidden'};
  transform: translateY(-50%) translateX(${props => props.$visible ? '0' : '10px'});
  transition: all 0.3s ease;
  z-index: 1001;
  border: 1px solid rgba(184, 134, 11, 0.3);

  &::after {
    content: '';
    position: absolute;
    left: 100%;
    top: 50%;
    transform: translateY(-50%);
    border: 6px solid transparent;
    border-left-color: rgba(0, 0, 0, 0.9);
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

export const BettyChatButton: React.FC<Props> = ({ onClick, hasUnreadMessages = false }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const { currentTrack } = useAudio();
  
  // Check if audio player is visible (when there's a current track)
  const hasAudioPlayer = !!currentTrack;

  const buttonVariants = {
    hover: { scale: 1.1 },
    tap: { scale: 0.95 }
  };

  return (
    <>
      <ChatButton
        $hasUnread={hasUnreadMessages}
        $hasAudio={hasAudioPlayer}
        onClick={onClick}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        variants={buttonVariants}
        whileHover="hover"
        whileTap="tap"
        title="Chat with Betty"
      >
        🎵
        <NotificationBadge $visible={hasUnreadMessages} />
      </ChatButton>
      
      <Tooltip $visible={showTooltip}>
        Ask Betty about music licensing
      </Tooltip>
    </>
  );
};